import React, { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  GraduationCap,
  ClipboardList,
  Shield,
  FileText,
  TrendingUp,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Award,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import type { OficialDesempenho } from "../../../../types/desempenho";

interface OficiaisTabProps {
  data: OficialDesempenho | null;
}

const formatMin = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
};

const formatDateBR = (dateStr: string) => {
  if (!dateStr) return "—";
  const parts = dateStr.split("T")[0].split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const statusBadge = (status: string) => {
  const s = status?.toLowerCase();
  if (s === "co" || s === "concluida")
    return <Badge className="bg-green-100 text-green-800 border-green-200">Concluída</Badge>;
  if (s === "ic" || s === "in" || s === "incompleta")
    return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Incompleta</Badge>;
  if (s === "ca" || s === "cancelada")
    return <Badge className="bg-red-100 text-red-800 border-red-200">Cancelada</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
};

function SectionCard({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-[oklch(0.45_0.15_142)]" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function StatRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}

// ─── Sub-seções ──────────────────────────────────────────────────────────────

function TreinamentosSection({ data }: { data: OficialDesempenho["treinamentos"] }) {
  const [expandedTipos, setExpandedTipos] = useState<Set<string>>(new Set());

  const toggle = (nome: string) => {
    setExpandedTipos(prev => {
      const next = new Set(prev);
      next.has(nome) ? next.delete(nome) : next.add(nome);
      return next;
    });
  };

  return (
    <SectionCard title="Treinamentos" icon={GraduationCap}>
      <div className="space-y-4">
        {/* Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Aplicados",      value: data.totalAplicados,       color: "" },
            { label: "Aprovados",      value: data.totalAprovados,       color: "text-green-600" },
            { label: "Reprovados",     value: data.totalReprovados,      color: "text-red-600" },
            { label: "Rel. Aprovados", value: data.relatoriosAprovados,  color: "text-blue-600" },
          ].map(({ label, value, color }) => (
            <Card key={label}>
              <CardHeader className="pb-2">
                <CardDescription>{label}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${color}`}>{value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabela expansível por tipo */}
        {data.porTipo.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">Por Tipo de Treinamento</p>
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Tipo</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Aplicados</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Aprovados</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Reprovados</th>
                  </tr>
                </thead>
                <tbody>
                  {data.porTipo.map(tipo => (
                    <React.Fragment key={tipo.nome}>
                      <tr
                        className="border-t border-border cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => toggle(tipo.nome)}
                      >
                        <td className="px-3 py-2 flex items-center gap-1">
                          {expandedTipos.has(tipo.nome) ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                          {tipo.nome}
                        </td>
                        <td className="text-right px-3 py-2 font-medium">{tipo.aplicados}</td>
                        <td className="text-right px-3 py-2 text-green-600">{tipo.aprovados}</td>
                        <td className="text-right px-3 py-2 text-red-600">{tipo.reprovados}</td>
                      </tr>
                      {expandedTipos.has(tipo.nome) && tipo.porDia.map(dia => (
                        <tr key={dia.data} className="bg-muted/20 border-t border-border/50 text-xs">
                          <td className="px-3 py-1.5 pl-8 text-muted-foreground">{formatDateBR(dia.data)}</td>
                          <td className="text-right px-3 py-1.5">{dia.aplicados}</td>
                          <td className="text-right px-3 py-1.5 text-green-600">{dia.aprovados}</td>
                          <td className="text-right px-3 py-1.5 text-red-600">{dia.reprovados}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.porTipo.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">Nenhum treinamento realizado no período.</p>
        )}
      </div>
    </SectionCard>
  );
}

function FilasSection({ data }: { data: OficialDesempenho["filas"] }) {
  return (
    <SectionCard title="Filas" icon={ClipboardList}>
      <div className="space-y-3">
        <StatRow label="Tempo como responsável" value={formatMin(data.tempoTotalMinutos)} />
        <StatRow label="Treinamentos enviados"  value={data.treinamentosEnviados} />
        <StatRow label="Ausências marcadas"     value={data.ausencias} />

        {data.porPeriodo.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">Por Dia</p>
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Data</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Tempo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.porPeriodo.map(p => (
                    <tr key={p.referencia} className="border-t border-border">
                      <td className="px-3 py-1.5">{formatDateBR(p.referencia)}</td>
                      <td className="text-right px-3 py-1.5 font-medium">{formatMin(p.tempoMinutos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.tempoTotalMinutos === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">Sem dados de fila no período.</p>
        )}
      </div>
    </SectionCard>
  );
}

function PatrulhasSection({ data }: { data: OficialDesempenho["patrulhas"] }) {
  const [expandedResp, setExpandedResp] = useState(false);
  const [expandedPat, setExpandedPat] = useState(false);

  const totalResp = data.patrulhasComoResponsavel.length;
  const { concluidas, incompletas, canceladas, porDia } = data.patrulhasComoPatrulheiro;
  const totalPat = concluidas + incompletas + canceladas;

  return (
    <SectionCard title="Patrulhas" icon={Shield}>
      <div className="space-y-4">
        {/* Tempo por função */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {(["supervisor", "monitor", "auxiliar"] as const).map(f => {
            const entry = data.tempoPorFuncao.find(t => t.funcao === f);
            return (
              <Card key={f}>
                <CardHeader className="pb-2">
                  <CardDescription className="capitalize">{f}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{entry ? formatMin(entry.tempoTotalMinutos) : "0m"}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Como responsável */}
        <div>
          <button
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 hover:text-foreground transition-colors"
            onClick={() => setExpandedResp(v => !v)}
          >
            {expandedResp ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Como Responsável ({totalResp} patrulha{totalResp !== 1 ? "s" : ""})
          </button>
          {expandedResp && totalResp > 0 && (
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Data</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Hora</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Duração</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Militares</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Função</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.patrulhasComoResponsavel.map(p => (
                    <tr key={p.id} className="border-t border-border">
                      <td className="px-3 py-1.5">{formatDateBR(p.data)}</td>
                      <td className="px-3 py-1.5">{p.hora}</td>
                      <td className="text-right px-3 py-1.5">{formatMin(p.duracaoMinutos)}</td>
                      <td className="text-right px-3 py-1.5">{p.militares}</td>
                      <td className="px-3 py-1.5 capitalize">{p.funcao}</td>
                      <td className="px-3 py-1.5">{statusBadge(p.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {expandedResp && totalResp === 0 && (
            <p className="text-sm text-muted-foreground pl-5">Nenhuma patrulha como responsável.</p>
          )}
        </div>

        {/* Como patrulheiro */}
        <div>
          <button
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 hover:text-foreground transition-colors"
            onClick={() => setExpandedPat(v => !v)}
          >
            {expandedPat ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Como Patrulheiro ({totalPat} patrulha{totalPat !== 1 ? "s" : ""})
          </button>

          {/* totais resumidos */}
          <div className="flex gap-4 mb-2">
            <div className="flex items-center gap-1.5 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-muted-foreground">CO:</span>
              <span className="font-semibold text-green-600">{concluidas}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <Clock className="h-4 w-4 text-yellow-500" />
              <span className="text-muted-foreground">IN:</span>
              <span className="font-semibold text-yellow-600">{incompletas}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <XCircle className="h-4 w-4 text-red-500" />
              <span className="text-muted-foreground">CA:</span>
              <span className="font-semibold text-red-600">{canceladas}</span>
            </div>
          </div>

          {expandedPat && porDia.length > 0 && (
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Data</th>
                    <th className="text-right px-3 py-2 font-medium text-green-700">Completo</th>
                    <th className="text-right px-3 py-2 font-medium text-yellow-700">Incompleto</th>
                    <th className="text-right px-3 py-2 font-medium text-red-700">Cancelado</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {porDia.map(d => (
                    <tr key={d.data} className="border-t border-border">
                      <td className="px-3 py-1.5">{formatDateBR(d.data)}</td>
                      <td className="text-right px-3 py-1.5 text-green-600 font-medium">{d.concluidas}</td>
                      <td className="text-right px-3 py-1.5 text-yellow-600 font-medium">{d.incompletas}</td>
                      <td className="text-right px-3 py-1.5 text-red-600 font-medium">{d.canceladas}</td>
                      <td className="text-right px-3 py-1.5 font-semibold">{d.concluidas + d.incompletas + d.canceladas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {expandedPat && porDia.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhuma patrulha como patrulheiro no período.</p>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

function RMSection({ data }: { data: OficialDesempenho["rm"] }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <SectionCard title="RM — Relatório de Movimento" icon={FileText}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Tempo responsável</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatMin(data.tempoTotalMinutos)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Relatórios enviados</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data.totalRelatorios}</div>
            </CardContent>
          </Card>
        </div>

        {data.porDia.length > 0 && (
          <div>
            <button
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 hover:text-foreground transition-colors"
              onClick={() => setExpanded(v => !v)}
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              Detalhe por Dia
            </button>
            {expanded && (
              <div className="rounded-md border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Data</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Tempo</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Relatórios</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.porDia.map(d => (
                      <tr key={d.data} className="border-t border-border">
                        <td className="px-3 py-1.5">{formatDateBR(d.data)}</td>
                        <td className="text-right px-3 py-1.5">{formatMin(d.tempoMinutos)}</td>
                        <td className="text-right px-3 py-1.5">{d.relatorios}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {data.tempoTotalMinutos === 0 && data.totalRelatorios === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">Sem dados de RM no período.</p>
        )}
      </div>
    </SectionCard>
  );
}

function RMOSection({ data }: { data: OficialDesempenho["rmo"] }) {
  return (
    <SectionCard title="RMO — Relatório de Movimento Oficial" icon={TrendingUp}>
      <div className="space-y-4">
        {/* Presença */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Presença Diária</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Card className="shadow-none">
              <CardContent className="pt-3 pb-3 px-3">
                <p className="text-xs text-muted-foreground">Total Ativo</p>
                <p className="text-xl font-bold">{formatMin(data.presenca.totalMinutosAtivo)}</p>
              </CardContent>
            </Card>
            <Card className="shadow-none">
              <CardContent className="pt-3 pb-3 px-3">
                <p className="text-xs text-muted-foreground">Em Atividade</p>
                <p className="text-xl font-bold">{formatMin(data.presenca.totalMinutosEmAtividade)}</p>
              </CardContent>
            </Card>
          </div>

          {data.presenca.porDia.length > 0 && (
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Data</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Ativo</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">Em Atividade</th>
                  </tr>
                </thead>
                <tbody>
                  {data.presenca.porDia.map(d => (
                    <tr key={d.data} className="border-t border-border">
                      <td className="px-3 py-1.5">{formatDateBR(d.data)}</td>
                      <td className="text-right px-3 py-1.5">{formatMin(d.minutosAtivo)}</td>
                      <td className="text-right px-3 py-1.5">{formatMin(d.minutosEmAtividade)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Responsável RMO */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Responsável RMO</p>
          {data.responsavel.totalDias > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                <span className="font-bold text-foreground">{data.responsavel.totalDias}</span>{" "}
                dia{data.responsavel.totalDias !== 1 ? "s" : ""} responsável —{" "}
                <span className="font-semibold text-foreground">{formatMin(data.responsavel.tempoTotalMinutos)}</span> total
              </p>
              {data.responsavel.porDia.length > 0 && (
                <div className="rounded-md border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">Data</th>
                        <th className="text-right px-3 py-2 font-medium text-muted-foreground">Tempo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.responsavel.porDia.map(d => (
                        <tr key={d.data} className="border-t border-border">
                          <td className="px-3 py-1.5">{formatDateBR(d.data)}</td>
                          <td className="text-right px-3 py-1.5 font-medium">{formatMin(d.tempoMinutos)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Não foi responsável pelo RMO no período.</p>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

function GruposExternosSection({ data }: { data: OficialDesempenho["gruposExternos"] }) {
  const [expandedGrupos, setExpandedGrupos] = useState<Set<string>>(new Set());
  const [expandedAceite, setExpandedAceite] = useState<Set<string>>(new Set());
  const [expandedAcoes, setExpandedAcoes] = useState<Set<string>>(new Set());

  const toggleGrupo = (id: string) => {
    setExpandedGrupos(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAceite = (id: string) => {
    setExpandedAceite(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAcoes = (id: string) => {
    setExpandedAcoes(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  if (data.length === 0) {
    return (
      <SectionCard title="Grupos" icon={Users}>
        <p className="text-sm text-muted-foreground text-center py-4">Oficial não pertence a nenhum grupo.</p>
      </SectionCard>
    );
  }

  // Agrupar por tipo
  const tiposMap = new Map<string, typeof data>();
  data.forEach(g => {
    if (!tiposMap.has(g.tipoGrupo)) tiposMap.set(g.tipoGrupo, []);
    tiposMap.get(g.tipoGrupo)!.push(g);
  });

  return (
    <SectionCard title="Grupos" icon={Users}>
      <div className="space-y-5">
        {Array.from(tiposMap.entries()).map(([tipo, grupos]) => (
          <div key={tipo}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 border-b border-border pb-1">
              {tipo}
            </p>
            <div className="space-y-3">
              {grupos.map(grupo => (
                <div key={grupo.idGrupo} className="rounded-lg border border-border overflow-hidden">
                  {/* Header do grupo */}
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-muted/20 hover:bg-muted/40 transition-colors text-left"
                    onClick={() => toggleGrupo(grupo.idGrupo)}
                  >
                    <span className="flex items-center gap-2 font-medium text-sm">
                      {grupo.imgGrupo && (
                        <img src={grupo.imgGrupo} alt={grupo.nomeGrupo} className="h-6 w-auto object-contain" />
                      )}
                      {grupo.nomeGrupo}
                    </span>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{grupo.convocacoes.total} convocaç{grupo.convocacoes.total !== 1 ? "ões" : "ão"}</span>
                      <span>{grupo.aceiteAcoes.totalAprovadas + grupo.aceiteAcoes.totalReprovadas} aceites</span>
                      <span>{grupo.acoesRealizadas.total} aç{grupo.acoesRealizadas.total !== 1 ? "ões" : "ão"}</span>
                      {expandedGrupos.has(grupo.idGrupo) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>
                  </button>

                  {expandedGrupos.has(grupo.idGrupo) && (
                    <div className="p-4 space-y-4">
                      {/* Convocações */}
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Convocações</p>
                        {grupo.convocacoes.total > 0 ? (
                          <p className="text-sm">
                            <span className="font-bold">{grupo.convocacoes.total}</span> convocaç{grupo.convocacoes.total !== 1 ? "ões realizadas" : "ão realizada"} no período.
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">Nenhuma convocação no período.</p>
                        )}
                      </div>

                      {/* Aceite de ações */}
                      <div>
                        <button
                          className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 hover:text-foreground transition-colors"
                          onClick={() => toggleAceite(grupo.idGrupo)}
                        >
                          {expandedAceite.has(grupo.idGrupo) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          Aceite de Ações ({grupo.aceiteAcoes.totalAprovadas} aprovadas / {grupo.aceiteAcoes.totalReprovadas} reprovadas)
                        </button>
                        {expandedAceite.has(grupo.idGrupo) && grupo.aceiteAcoes.acoes.length > 0 && (
                          <div className="rounded-md border border-border overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/40">
                                <tr>
                                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Ação</th>
                                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
                                  <th className="text-right px-3 py-2 font-medium text-muted-foreground">Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {grupo.aceiteAcoes.acoes.map(a => (
                                  <tr key={a.id} className="border-t border-border">
                                    <td className="px-3 py-1.5">{a.descricaoAcao}</td>
                                    <td className="px-3 py-1.5">
                                      {a.status === "aprovado"
                                        ? <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">Aprovado</Badge>
                                        : <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">Reprovado</Badge>
                                      }
                                    </td>
                                    <td className="text-right px-3 py-1.5">{formatDateBR(a.decididoEm)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {expandedAceite.has(grupo.idGrupo) && grupo.aceiteAcoes.acoes.length === 0 && (
                          <p className="text-xs text-muted-foreground pl-5">Nenhum aceite/reprovação no período.</p>
                        )}
                      </div>

                      {/* Ações realizadas */}
                      <div>
                        <button
                          className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 hover:text-foreground transition-colors"
                          onClick={() => toggleAcoes(grupo.idGrupo)}
                        >
                          {expandedAcoes.has(grupo.idGrupo) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          Ações Realizadas ({grupo.acoesRealizadas.total})
                        </button>
                        {expandedAcoes.has(grupo.idGrupo) && grupo.acoesRealizadas.acoes.length > 0 && (
                          <div className="rounded-md border border-border overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/40">
                                <tr>
                                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Ação</th>
                                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Aprovação</th>
                                  <th className="text-right px-3 py-2 font-medium text-muted-foreground">Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {grupo.acoesRealizadas.acoes.map(a => (
                                  <tr key={a.id} className="border-t border-border">
                                    <td className="px-3 py-1.5">{a.descricaoAcao}</td>
                                    <td className="px-3 py-1.5">
                                      {a.statusAprovacao === "aprovado"  && <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">Aprovado</Badge>}
                                      {a.statusAprovacao === "rejeitado" && <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">Reprovado</Badge>}
                                      {a.statusAprovacao === "pendente"  && <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 text-xs">Pendente</Badge>}
                                      {a.statusAprovacao === null        && <Badge variant="secondary" className="text-xs">—</Badge>}
                                    </td>
                                    <td className="text-right px-3 py-1.5">{formatDateBR(a.criadoEm)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {expandedAcoes.has(grupo.idGrupo) && grupo.acoesRealizadas.acoes.length === 0 && (
                          <p className="text-xs text-muted-foreground pl-5">Nenhuma ação criada no período.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function PromocoesSection({ data }: { data: OficialDesempenho["promocoes"] }) {
  return (
    <SectionCard title="Promoções/Rebaixamentos" icon={Award}>
      {data.total > 0 ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground mb-3">
            <span className="font-bold text-foreground">{data.total}</span> promoç{data.total !== 1 ? "ões" : "ão"}/rebaixamento{data.total !== 1 ? "s" : ""} no período.
          </p>
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Militar Promovido</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Patente</th>
                  <th className="text-right px-3 py-2 font-medium text-muted-foreground">Data</th>
                </tr>
              </thead>
              <tbody>
                {data.lista.map(p => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-3 py-1.5 font-medium">{p.militarPromovido}</td>
                    <td className="px-3 py-1.5">
                      <Badge variant="secondary">{p.patenteRecebida}</Badge>
                    </td>
                    <td className="text-right px-3 py-1.5 text-muted-foreground">{formatDateBR(p.data)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-4">Nenhuma promoção realizada no período.</p>
      )}
    </SectionCard>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export function OficiaisTab({ data }: OficiaisTabProps) {
  if (!data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Selecione um oficial e clique em <strong>Buscar</strong> para visualizar os dados.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header do oficial */}
      <div className="flex items-center gap-4 p-4 rounded-xl border border-[oklch(0.45_0.15_142)]/30 bg-[oklch(0.45_0.15_142)]/5">
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-muted">
          <img
            src={`https://www.habbo.com.br/habbo-imaging/avatarimage?user=${encodeURIComponent(data.oficial.nome)}&action=std&direction=3&head_direction=3&img_format=png&gesture=sml&headonly=1&size=l`}
            alt={data.oficial.nome}
            className="h-full w-full object-contain"
            style={{ imageRendering: "pixelated" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
        <div>
          <p className="font-semibold text-foreground text-lg leading-tight">{data.oficial.nome}</p>
          <p className="text-sm text-[oklch(0.45_0.15_142)] font-medium">{data.oficial.patente}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {data.oficial.diasNaPatente > 0
              ? `Há ${data.oficial.diasNaPatente} dia${data.oficial.diasNaPatente !== 1 ? "s" : ""} nesta patente`
              : "Recém promovido"}
          </p>
        </div>
      </div>

      <TreinamentosSection   data={data.treinamentos} />
      <FilasSection          data={data.filas} />
      <PatrulhasSection      data={data.patrulhas} />
      <RMSection             data={data.rm} />
      <RMOSection            data={data.rmo} />
      <GruposExternosSection data={data.gruposExternos} />
      <PromocoesSection      data={data.promocoes} />
    </div>
  );
}
